import type { DefectRestriction, DefectStatus } from './constants'

// A row of GET /api/aircraft-defects.
export interface AircraftDefect {
  defectId: number
  registration: string
  message: string
  status: DefectStatus
  restriction: DefectRestriction[]
  reportedBy: string | null
  reportedByName: string | null
  reportedAt: string
  triagedAt: string | null
  resolvedAt: string | null
  // The AOG window (YYYY-MM-DD, last day inclusive) set when a grounding
  // defect is published; null otherwise.
  aogFrom: string | null
  aogUntil: string | null
  attachmentCount: number
}

export interface AircraftDefectAttachment {
  attachmentId: number
  defectId: number
  filename: string
  contentType: string
  uploadedAt: string
}

// The traffic light on the fleet page: red when a published defect grounds
// the aircraft, amber when open defects restrict it, green otherwise.
//  - published: any defect is at least a warning, a 'grounded' tag is AOG;
//  - held: the aircraft is released to service, so a held defect never
//    grounds it (its 'grounded' tag is ignored) but still counts as a
//    warning while it carries another restriction;
//  - pending and resolved are not public / are closed, and do not count.
export type AirworthinessLight = 'aog' | 'restricted' | 'airworthy'

type DefectLike = Pick<AircraftDefect, 'status' | 'restriction'>

// The restrictions a held defect still imposes.
export const holdRestrictions = (d: DefectLike) => d.restriction.filter((r) => r !== 'grounded')

export function airworthinessLight(defects: ReadonlyArray<DefectLike>): AirworthinessLight {
  const published = defects.filter((d) => d.status === 'published')
  if (published.some((d) => d.restriction.includes('grounded'))) return 'aog'
  const restrictedByHold = defects.some((d) => d.status === 'held' && holdRestrictions(d).length > 0)
  return published.length > 0 || restrictedByHold ? 'restricted' : 'airworthy'
}

// The calendar block an AOG aircraft carries, derived from its defects.
//   from   first blocked day (YYYY-MM-DD)
//   until  last blocked day; null once the period has lapsed with the defect
//          still grounding the aircraft: the block stays open-ended until
//          triage extends or resolves it
export interface AogBlock {
  from: string
  until: string | null
  elapsed: boolean
}

export function aogBlock(
  defects: ReadonlyArray<Pick<AircraftDefect, 'status' | 'restriction' | 'aogFrom' | 'aogUntil'>>,
  today: string,
): AogBlock | null {
  const grounding = defects.filter(
    (d) => d.status === 'published' && d.restriction.includes('grounded') && d.aogFrom && d.aogUntil,
  )
  if (grounding.length === 0) return null
  const from = grounding.map((d) => d.aogFrom as string).sort()[0] as string
  const until = grounding.map((d) => d.aogUntil as string).sort().at(-1) as string
  const elapsed = until < today
  return { from, until: elapsed ? null : until, elapsed }
}

// Does a booking spanning these club dates (inclusive) touch the block?
export function overlapsAog(block: AogBlock | null, startDate: string, endDate: string): boolean {
  if (!block) return false
  return endDate >= block.from && (block.until === null || startDate <= block.until)
}
